import React, { createContext, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import {
  useShopifyPet,
  useCartLineItemForPet,
  useMealPlansCombinations,
} from '@web/shopify';
import { useShopifyExtras } from '@web/shopify/useShopifyExtras';
import { useReporter } from '../../reporter';
import { useToast } from '../../Toast';
import {
  useExperiment,
  ONBOARDING_FLOW_UI,
  HIDE_MIXED,
} from '../../experiments';
import {
  ADD_PUP_SUBPATH,
  EDIT_PET_SUBPATH,
  DELETE_PET_SUBPATH,
  USER_INFO_SLUG,
} from '../routing';
import { useShopifyCartContext } from '../OnboardingFlowContainer/ShopifyCartProvider';
import { useCampaign } from '../../campaign';
import { isCompletePet } from './guard';
import { useMachine, optimizedOnboardingMachine } from './state';
import { STATE, ACTIONS } from './constants';

const initialContext = {};
export const OptimizedOnboardingContext = createContext(initialContext);

export const OptimizedOnboardingProvider = ({ children }) => {
  const router = useRouter();
  const reporter = useReporter();
  const [showModal, setShowModal] = useState(false);
  const [personalizedPlan] = useState(false);
  const [subpath] = router.query.path ?? [];
  const isEditPet = subpath === EDIT_PET_SUBPATH;
  const [showPlanSelection, setShowPlanSelection] = useState(isEditPet);
  const {
    cart,
    addPetData,
    attachCustomer,
    attachExtras,
    isLoading: isShopifyCartLoading,
    deletePetFromCart,
    updateCartAttributes,
    ...cartApi
  } = useShopifyCartContext();
  const { campaignId } = useCampaign();
  const { showToast } = useToast();

  const pet_id = router.query?.pet_id;
  const isNewPet = pet_id === 'new_pet';
  const {
    pet,
    isLoading: petIsLoading,
    savePet,
  } = useShopifyPet(!isNewPet ? pet_id : undefined);

  const currentMachine = optimizedOnboardingMachine;
  const [state, send, service] = useMachine(currentMachine);
  //if the pet already has a starterbox that means the user has already seen this page (edit pup)
  const completedPet = isCompletePet(pet);
  const editPetFlow = useRef(isEditPet);

  // TODO: Saved in state after meal plan selection in ES-1582
  const mealplanId = state.context.mealplanId;
  const { lineItemForPet: mealPlanForPet, addonsForPet } =
    useCartLineItemForPet(pet_id);

  const fetchCombinations = completedPet || isEditPet;

  // Add meal plan combinations hook
  const {
    combinationsByPlanType,
    isLoading: isCombinationsLoading,
    error: combinationsError,
    refreshPlans,
  } = useMealPlansCombinations(fetchCombinations ? pet_id : null);

  const {
    treats: treatProducts,
    supplements: supplementProducts,
    isLoading: isExtrasLoading,
    error: extrasError,
    refreshExtras,
  } = useShopifyExtras(completedPet ? pet_id : null);

  // Update loading state to include combinations loading
  const isLoading =
    isShopifyCartLoading ||
    isCombinationsLoading ||
    isExtrasLoading ||
    petIsLoading;

  const goBack = () => {
    send(ACTIONS.goBack());
  };

  // TODO: Return live data via useMealPlanProductRecommendations hook ES-1479

  const returnToQuiz = slug => {
    // Handling when the function is called by element onClick handler which
    // passes an event object. We are only interested in this argument if its a string.
    if (typeof slug !== 'string') slug = USER_INFO_SLUG;

    router.push(`/onboarding/quiz/${slug}/?pet_id=${pet_id}`);
  };

  const goToLogin = () => {
    router.push(`/auth/login/`);
    showToast({
      status: 'success',
      message:
        'We found an account associated with your email. Please log in to access your account.',
    });
  };

  const { value: onboardingFlowUI } = useExperiment(
    ONBOARDING_FLOW_UI,
    'control',
  );

  const { value: hideMixed } = useExperiment(HIDE_MIXED, false);

  useEffect(() => {
    if (!isLoading && state.name === STATE.initial) {
      send(
        ACTIONS.initialLoad({
          customer: cart?.customer,
          pets: cart?.pets ?? [],
          pet: pet,
          pet_id,
          cart,
          flags: { onboardingFlowUI, hideMixed },
          isAddPet: subpath === ADD_PUP_SUBPATH,
          isEditPet: isEditPet,
          isDeletePet: subpath === DELETE_PET_SUBPATH,
          deletePetFromCart,
        }),
      );
    }
  }, [
    state,
    cart,
    send,
    pet,
    pet_id,
    isLoading,
    petIsLoading,
    personalizedPlan,
    deletePetFromCart,
    isEditPet,
    subpath,
    onboardingFlowUI,
    hideMixed,
  ]);

  // Update cart attributes with campaign_id when cart is first created
  useEffect(() => {
    if (
      campaignId &&
      cart?.cart_id &&
      updateCartAttributes &&
      !cart?.__cart_data__?.attributes?.campaign_id
    ) {
      updateCartAttributes({ campaign_id: campaignId });
    }
  }, [
    campaignId,
    cart?.cart_id,
    cart?.__cart_data__?.attributes?.campaign_id,
    updateCartAttributes,
  ]);

  const planSelectionProps = {
    combinationsByPlanType,
    isCombinationsLoading,
    combinationsError,
    refreshPlans,
    returnToQuiz,
  };

  const values = {
    ...planSelectionProps,
    petIsLoading,
    extrasError,
    refreshExtras,
    pets: cart?.pets,
    pet,
    savePet,
    cartApi,
    addPetData,
    deletePetFromCart,
    cart,
    customer: cart?.customer,
    mealplanId,
    combinationsByPlanType,
    mealPlanForPet,
    treatProducts,
    supplementProducts,
    addonsForPet,
    attachCustomer,
    attachExtras,
    updateCartAttributes,
    isLoading,
    setLocalSelection: () => {},
    send,
    service,
    pet_id: state.context.pet_id,
    nextKey: state.context.nextKey,
    prevState: state.context.prevState,
    selectedAddons: addonsForPet,
    selectedPlanType: state.context.planType,
    selectedFreshRecipes: state.context.recipes?.fresh,
    selectedDryRecipes: state.context.recipes?.dry,
    stateKey: state.name,
    reporter,
    showModal,
    setShowModal,
    personalizedPlan,
    goBack,
    goToLogin,
    showToast,
    pricePeriod: 'week',
    quizStepNumbers: {
      pupInfo: 1,
      calories: 2,
      activity: 3,
      userInfo: 4,
    },
    showPlanSelection,
    setShowPlanSelection,
    editPetFlow,
    flags: {
      onboardingFlowUI,
      hideMixed,
    },
    campaignId,
  };

  return (
    <OptimizedOnboardingContext.Provider value={values}>
      {children}
    </OptimizedOnboardingContext.Provider>
  );
};

OptimizedOnboardingProvider.propTypes = {
  children: PropTypes.object.isRequired,
};
